<template>
    <div class="page-container">
        <PageTitle :title="'Historique des caisses'" :description="total + ' caisse(s) au total'" />
        <div class="content-section">
            <div class="filters-section">
                <div class="filters-wrapper">
                    <div class="filter-group">
                        <input type="date" v-model="startDate" @change="onDateChange" class="filter-input"
                            placeholder="Date début">
                        <input type="date" v-model="endDate" @change="onDateChange" class="filter-input"
                            placeholder="Date fin">
                        <select v-model="selectedStatus" @change="onStatusChange" class="filter-input">
                            <option value="">Tous les statuts</option>
                            <option value="open">Ouvertes</option>
                            <option value="closed">Fermées</option>
                        </select>
                    </div>
                    <BaseButton v-if="hasActiveFilters" @click="clearAllFilters" class="btn-reset">
                        <i class="fa-solid fa-trash"></i>
                        Réinitialiser
                    </BaseButton>
                </div>
            </div>

            <div v-if="loading" class="loading-state">
                <div class="loading-spinner"></div>
                <p>Chargement des caisses...</p>
            </div>

            <div v-else-if="!loading && cashRegisters.length === 0" class="empty-state">
                <div class="empty-icon">
                    <i class="fa-solid fa-cash-register" style="font-size: 3rem;"></i>
                </div>
                <h3>Aucune caisse trouvée</h3>
                <p>{{ hasActiveFilters ? 'Aucune caisse ne correspond à vos critères' : 'Aucune caisse enregistrée' }}
                </p>
                <BaseButton v-if="hasActiveFilters" @click="clearAllFilters" class="btn-secondary">
                    Réinitialiser les filtres
                </BaseButton>
            </div>

            <div v-else class="registers-wrapper">
                <table class="registers-table">
                    <thead>
                        <tr>
                            <th scope="col">Caisse</th>
                            <th scope="col">Période</th>
                            <th scope="col">Montants</th>
                            <th scope="col">Utilisateurs</th>
                            <th scope="col" class="col-actions">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="register in cashRegisters" :key="register.id">
                            <td class="register-info-cell">
                                <div class="register-id">Caisse #{{ register.id }}</div>
                                <BaseBadge :class="['status-badge', `status-${register.status}`]">
                                    {{ getStatusLabel(register.status) }}
                                </BaseBadge>
                            </td>
                            <td class="period-cell">
                                <div class="period-item">
                                    <span class="period-label">Ouverture</span>
                                    <span class="period-value">{{ formatDate(register.start_date) }}</span>
                                </div>
                                <div v-if="register.end_date" class="period-item">
                                    <span class="period-label">Fermeture</span>
                                    <span class="period-value">{{ formatDate(register.end_date) }}</span>
                                </div>
                            </td>
                            <td class="amounts-cell">
                                <div class="amount-row">
                                    <span class="amount-label">Fond:</span>
                                    <span class="amount-value">{{ formatCurrency(register.start_balance) }}</span>
                                </div>
                                <div class="amount-row">
                                    <span class="amount-label">À déposer:</span>
                                    <span class="amount-value">{{ formatCurrency(register.total_cash_balance) }}</span>
                                </div>
                                <div v-if="register.end_balance" class="amount-row">
                                    <span class="amount-label">Déposé:</span>
                                    <span class="amount-value">{{ formatCurrency(register.end_balance) }}</span>
                                </div>
                                <div v-if="register.balance_difference !== null" class="amount-row">
                                    <span class="amount-label">Différence:</span>
                                    <span class="amount-value" :class="getDifferenceClass(register.balance_difference)">
                                        {{ formatCurrency(register.balance_difference) }}
                                    </span>
                                </div>
                            </td>
                            <td class="users-cell">
                                <div class="user-row">
                                    <span class="user-label">Ouvert par:</span>
                                    <span class="user-value">{{ register.open_by?.name || '-' }}</span>
                                </div>
                                <div v-if="register.close_by" class="user-row">
                                    <span class="user-label">Fermé par:</span>
                                    <span class="user-value">{{ register.close_by?.name }}</span>
                                </div>
                            </td>
                            <td class="actions-cell">
                                <div class="actions-dropdown">
                                    <BaseDropdown position="bottom-right" width="md">
                                        <template #trigger>
                                            <BaseButton type="button" variant="icon" size="sm" class="dropdown-trigger">
                                                <i class="fa-solid fa-ellipsis-v"></i>
                                            </BaseButton>
                                        </template>
                                        <BaseButton type="button" variant="ghost" @click="downloadSalesSheet(register)" class="dropdown-item"
                                            :disabled="downloadingSalesSheetId === register.id">
                                            <span v-if="downloadingSalesSheetId === register.id" class="spinner-small"></span>
                                            <i v-else class="fa-solid fa-file"></i>
                                            Fiche de ventes
                                        </BaseButton>
                                        <BaseButton type="button" variant="ghost" @click="downloadReport(register)" class="dropdown-item"
                                            :disabled="downloadingReportId === register.id">
                                            <span v-if="downloadingReportId === register.id" class="spinner-small"></span>
                                            <i v-else class="fa-solid fa-download"></i>
                                            Rapport de caisse
                                        </BaseButton>
                                    </BaseDropdown>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <PaginationComponent v-if="!loading && cashRegisters.length > 0" :current-page="currentPage"
                :per-page="perPage" :total="total" :to="to" @page-change="onPageChange" />
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import CashRegisterService from '@/services/cashRegisterService'
import { useToastStore } from '@/store/modules/toastStore'
import PageTitle from '../common/PageTitle.vue'
import BaseDropdown from '@/components/base/BaseDropdown.vue'
import BaseBadge from '@/components/base/BaseBadge.vue'

export default {
    name: 'CashRegistersHistory',
    components: {
        PaginationComponent,
        PageTitle,
        BaseDropdown,
        BaseBadge
    },
    props: {
        perPageDefault: {
            type: Number,
            default: 10
        }
    },
    setup(props) {
        const toastStore = useToastStore()
        const cashRegisters = ref([])
        const loading = ref(false)
        const startDate = ref('')
        const endDate = ref('')
        const selectedStatus = ref('')
        const currentPage = ref(1)
        const perPage = ref(props.perPageDefault)
        const total = ref(0)
        const to = ref(0)
        const downloadingReportId = ref(null)
        const downloadingSalesSheetId = ref(null)

        const hasActiveFilters = computed(() => {
            return startDate.value || endDate.value || selectedStatus.value
        })

        const fetchCashRegisters = async () => {
            loading.value = true
            try {
                const params = {
                    page: currentPage.value,
                    per_page: perPage.value
                }
                if (startDate.value) params.start_date = startDate.value
                if (endDate.value) params.end_date = endDate.value
                if (selectedStatus.value) params.status = selectedStatus.value

                const response = await CashRegisterService.getCashRegisters(params)
                cashRegisters.value = response.data
                currentPage.value = response.meta.current_page
                perPage.value = response.meta.per_page || perPage.value
                total.value = response.meta.total || 0
                to.value = response.meta.to || 0
            } catch (error) {
                toastStore.addToast({
                    type: 'error',
                    message: "Erreur lors du chargement des caisses",
                    duration: 4000
                })
            } finally {
                loading.value = false
            }
        }

        const onDateChange = () => {
            currentPage.value = 1
            fetchCashRegisters()
        }

        const onStatusChange = () => {
            currentPage.value = 1
            fetchCashRegisters()
        }

        const onPageChange = (page) => {
            currentPage.value = page
            fetchCashRegisters()
        }

        const clearAllFilters = () => {
            startDate.value = ''
            endDate.value = ''
            selectedStatus.value = ''
            currentPage.value = 1
            fetchCashRegisters()
        }

        const downloadSalesSheet = async (register) => {
            if (!register?.id) return

            try {
                downloadingSalesSheetId.value = register.id
                toastStore.addToast({
                    type: 'info',
                    message: 'Generation de la fiche de ventes...',
                    duration: 2500
                })
                await CashRegisterService.printSalesSheet(register.id, false)
                toastStore.addToast({
                    type: 'success',
                    message: 'Fiche de ventes téléchargée',
                    duration: 3000
                })
            } catch (error) {
                toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors du téléchargement',
                    duration: 4000
                })
            } finally {
                downloadingSalesSheetId.value = null
            }
        }

        const downloadReport = async (register) => {
            if (!register?.id) return

            try {
                downloadingReportId.value = register.id
                toastStore.addToast({
                    type: 'info',
                    message: 'Generation du rapport...',
                    duration: 2500
                })
                await CashRegisterService.downloadReport(register.id)
                toastStore.addToast({
                    type: 'success',
                    message: 'Rapport téléchargé',
                    duration: 3000
                })
            } catch (error) {
                toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors du téléchargement',
                    duration: 4000
                })
            } finally {
                downloadingReportId.value = null
            }
        }

        const formatDate = (date) => {
            if (!date) return '-'
            return new Date(date).toLocaleString('fr-FR', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        }

        const formatCurrency = (amount) => {
            if (amount === null || amount === undefined) return '-'
            return new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'XOF',
                minimumFractionDigits: 0
            }).format(amount).replace('XOF', 'F')
        }

        const getStatusLabel = (status) => {
            const labels = { open: 'Ouverte', closed: 'Fermée' }
            return labels[status] || status
        }

        const getStatusVariant = (status) => {
            const variants = { open: 'success', closed: 'gray' }
            return variants[status] || 'gray'
        }

        const getDifferenceClass = (difference) => {
            if (!difference || difference === 0) return ''
            return difference > 0 ? 'positive' : 'negative'
        }

        onMounted(() => {
            fetchCashRegisters()
        })

        return {
            // state
            cashRegisters,
            loading,
            startDate,
            endDate,
            selectedStatus,
            currentPage,
            perPage,
            total,
            to,
            downloadingReportId,
            downloadingSalesSheetId,
            hasActiveFilters,
            // methods
            onDateChange,
            onStatusChange,
            onPageChange,
            clearAllFilters,
            downloadSalesSheet,
            downloadReport,
            formatDate,
            formatCurrency,
            getStatusLabel,
            getStatusVariant,
            getDifferenceClass
        }
    }
}
</script>

<style scoped>
.page-container {
    margin: 0 auto;
}

.content-section {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #f1f5f9;
}

.filters-section {
    margin-bottom: 24px;
}

.filters-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
}

.filter-group {
    display: flex;
    gap: 12px;
}

.filter-input {
    padding: 10px 14px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    background: white;
    min-width: 150px;
    transition: all 0.2s;
}

.filter-input:focus {
    outline: none;
    border-color: #0A5C6B;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.btn-reset {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-reset:hover {
    background: #fecaca;
    transform: translateY(-1px);
}

.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 20px;
    color: #64748b;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #f3f4f6;
    border-top-color: #0A5C6B;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.empty-state {
    text-align: center;
    padding: 80px 20px;
    color: #64748b;
}

.empty-icon {
    color: #d1d5db;
    margin-bottom: 16px;
}

.empty-state h3 {
    font-size: 18px;
    color: #374151;
    margin-bottom: 8px;
}

.btn-secondary {
    margin-top: 16px;
    padding: 10px 20px;
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-secondary:hover {
    background: #f9fafb;
    border-color: #9ca3af;
}

.registers-wrapper {
    margin-left: -24px;
    margin-right: -24px;
    margin-bottom: 24px;
    overflow-x: auto;
}

.registers-table {
    width: 100%;
    border-collapse: collapse;
    background: white;
}

.registers-table thead {
    background: #f8fafc;
    border-bottom: 2px solid #e2e8f0;
}

.registers-table th {
    text-align: left;
    padding: 16px 24px;
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-right: 1px solid #f1f5f9;
}

.registers-table th:last-child,
.col-actions {
    border-right: none;
    text-align: center;
}

.registers-table tbody tr {
    background: white;
    transition: background 0.2s;
    border-bottom: 1px solid #f1f5f9;
}

.registers-table tbody tr:hover {
    background: #f8fafc;
}

.registers-table td {
    padding: 16px 24px;
    vertical-align: top;
    border-right: 1px solid #f1f5f9;
    display: table-cell;
}

.registers-table td:last-child {
    border-right: none;
}

.register-info-cell {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 180px;
}

.period-cell {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 200px;
}

.amounts-cell {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 240px;
}

.users-cell {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 200px;
}

.actions-cell {
    width: 100px;
    text-align: center;
}

.register-id {
    font-size: 15px;
    font-weight: 600;
    color: #1e293b;
}

.status-badge {
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    width: fit-content;
}

.status-open {
    background: #dcfce7;
    color: #166534;
}

.status-closed {
    background: #f3f4f6;
    color: #374151;
}

.period-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.period-label {
    font-size: 11px;
    color: #64748b;
    font-weight: 500;
}

.period-value {
    font-size: 13px;
    color: #1e293b;
    font-weight: 500;
}

.amount-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 2px 0;
}

.amount-label {
    font-size: 12px;
    color: #64748b;
}

.amount-value {
    font-size: 12px;
    font-weight: 600;
    color: #1e293b;
}

.amount-value.positive {
    color: #059669;
}

.amount-value.negative {
    color: #dc2626;
}

.user-row {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.user-label {
    font-size: 11px;
    color: #64748b;
    font-weight: 500;
}

.user-value {
    font-size: 13px;
    color: #1e293b;
    font-weight: 500;
}

.actions-dropdown {
    position: relative;
}

.dropdown-trigger {
    width: 32px;
    height: 32px;
    border: 1px solid #e2e8f0;
    background: white;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748b;
    transition: all 0.2s;
}

.dropdown-trigger:hover,
.dropdown-trigger.active {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #374151;
}

.dropdown-menu {
    position: absolute;
    top: 100%;
    right: 0;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    z-index: 100;
    min-width: 180px;
    overflow: hidden;
    margin-top: 4px;
}

.dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 12px 16px;
    border: none;
    background: white;
    color: #374151;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
    text-align: left;
}

.dropdown-item svg {
    color: #0A5C6B;
}

.dropdown-item:hover {
    background: #f8fafc;
}

.dropdown-item:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.spinner-small {
    animation: spin 1s linear infinite;
}

@media (max-width: 768px) {
    .page-container {
        padding: 0;
    }

    .content-section {
        border-radius: 0;
        padding: 16px;
    }

    .registers-wrapper {
        margin-left: -16px;
        margin-right: -16px;
    }

    .filters-wrapper {
        flex-direction: column;
        align-items: stretch;
    }

    .filter-group {
        flex-wrap: wrap;
    }
}
</style>